/* Homepage header at-top toggle moved to an inline script in layout/theme.liquid
   (2026-09-07): it must exist before the browser restores scroll on back-navigation. */

/* =========================================================
   Free-gift tier reconciler
   - Reads config JSON rendered by snippets/evol-gift-tier-config.liquid
   - On cart update: computes tier from subtotal (converted to GBP via
     Shopify.currency.rate), diffs current gift lines vs target, fires
     /cart/add.js or /cart/change.js to reconcile
   - Debounced 300ms (matches Dawn's ON_CHANGE_DEBOUNCE_TIMER)
   - Re-entrancy guarded via PUB_SUB event source
   ========================================================= */
(function () {
  const SOURCE = 'evol-gift-tier';
  const DEBOUNCE_MS = 300;

  const configEl = document.getElementById('evol-gift-tier-config');
  if (!configEl) return;

  let CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (err) {
    console.warn('[evol-gift] config JSON parse failed', err);
    return;
  }

  const GIFT_IDS = new Set(CONFIG.giftVariantIds);
  const MAX_GBP = CONFIG.thresholdsGbp[CONFIG.thresholdsGbp.length - 1];

  // Named-gift copy per tier transition. Index is the tier you're ABOUT TO unlock.
  const NEXT_TIER_COPY = {
    1: 'to unlock your first Mystery Pack',
    2: 'to unlock a 2nd Mystery Pack',
    3: 'to unlock 4 Mystery Packs + Ultra Pro sleeves',
    4: 'to unlock 8 Mystery Packs + 2 sleeves packs',
  };

  let reconciling = false;
  let debounceTimer = null;
  let lastTierN = null;
  // Persists the "just lost Tier N" state across the reconcile's multiple
  // renderBar calls + Dawn's refreshDawnCartSections DOM swap. Without this,
  // the banner flashes for ~300ms before being wiped by the section refresh.
  let lossState = null; // { tierN, shownAt }
  const LOSS_DURATION_MS = 6000;

  function rate() {
    const r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return Number.isFinite(r) && r > 0 ? r : 1;
  }

  function presentmentCentsToGbp(cents) {
    return cents / 100 / rate();
  }

  function gbpToPresentmentCents(gbp) {
    return Math.round(gbp * rate() * 100);
  }

  function determineTier(gbpSubtotal) {
    for (let i = CONFIG.tiers.length - 1; i >= 0; i--) {
      if (gbpSubtotal >= CONFIG.tiers[i].minGbp) return CONFIG.tiers[i];
    }
    return null;
  }

  function currentGiftLines(cart) {
    // Only counts auto-added gift lines as "legitimate current state".
    // Lines missing `_auto_added:true` (i.e. manually added via URL or direct POST)
    // are flagged separately by findTamperedGiftLines so reconcile removes them.
    const out = {};
    cart.items.forEach((item) => {
      if (!GIFT_IDS.has(item.variant_id)) return;
      const autoAdded = item.properties && item.properties._auto_added === 'true';
      if (!autoAdded) return;
      out[item.variant_id] = { quantity: item.quantity, key: item.key };
    });
    return out;
  }

  function findTamperedGiftLines(cart) {
    // A gift line is tampered if either (a) it lacks _auto_added:true (someone
    // POSTed /cart/add.js directly), or (b) its _gift_tier is absent/invalid
    // (not one of our real tier numbers). A mismatch between a VALID tier tag
    // and the cart's current tier is NOT tampering — it's legitimate stale
    // state from a prior tier, and the regular qty reconciliation handles it.
    const validTiers = CONFIG.tiers.map((t) => String(t.n));
    return cart.items.filter((item) => {
      if (!GIFT_IDS.has(item.variant_id)) return false;
      const autoAdded = item.properties && item.properties._auto_added === 'true';
      if (!autoAdded) return true;
      const tierTag = item.properties && item.properties._gift_tier;
      if (!tierTag || validTiers.indexOf(tierTag) === -1) return true;
      return false;
    });
  }

  function desiredGiftLines(tier) {
    const out = {};
    if (!tier) return out;
    tier.lines.forEach((l) => {
      out[l.id] = l.qty;
    });
    return out;
  }

  function formatPresentment(cents, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0) + ' ' + currencyCode;
    }
  }

  // Reward pack note (owner, 7 Oct 2026): the free Reel Reward Pack needs £100+ of sealed products and the checkout
  // validator blocks below £98 (2% slack). main-cart-items.liquid renders the note on the pack's row (exact for GBP);
  // this re-decides it for every currency after each cart change. Gifts and the pack never count toward the £100.
  const REEL_ID = 62061318275402;
  function renderReelNote(cart) {
    const notes = document.querySelectorAll('[data-evol-reel-note]');
    if (!notes.length || !cart || !cart.items) return;
    const sealedCents = cart.items.reduce(function (sum, it) {
      return it.variant_id === REEL_ID || GIFT_IDS.has(it.variant_id) ? sum : sum + (it.final_line_price || 0);
    }, 0);
    const blocks = presentmentCentsToGbp(sealedCents) < 100 * 0.98;
    const addCents = Math.max(100, Math.ceil((gbpToPresentmentCents(100) - sealedCents) / 100) * 100);
    notes.forEach(function (el) {
      el.hidden = !blocks;
      const amt = el.querySelector('[data-evol-reel-note-add]');
      if (amt && blocks) amt.textContent = formatPresentment(addCents, cart.currency);
    });
  }

  function renderTierMsg(cart, gbpSubtotal, tier) {
    document.querySelectorAll('[data-evol-gift-tiermsg]').forEach((el) => {
      if (cart.item_count === 0) { el.hidden = true; return; }
      el.hidden = false;
      let html;
      if (tier && tier.n === 4) {
        html = '<strong>Maximum tier unlocked.</strong> Enjoy the extras';
      } else if (tier) {
        const next = CONFIG.tiers.find((t) => t.minGbp > gbpSubtotal);
        const remain = formatPresentment(gbpToPresentmentCents(next.minGbp - gbpSubtotal), cart.currency);
        html = '<strong>Tier ' + tier.n + ' unlocked.</strong> Add ' + remain + ' ' + NEXT_TIER_COPY[next.n];
      } else {
        const first = CONFIG.tiers[0];
        const remain = formatPresentment(gbpToPresentmentCents(first.minGbp - gbpSubtotal), cart.currency);
        html = 'Add <strong>' + remain + '</strong> ' + NEXT_TIER_COPY[1];
      }
      el.innerHTML = html;
    });
  }

  function renderBar(cart, gbpSubtotal, tier) {
    renderTierMsg(cart, gbpSubtotal, tier);
    const nodes = document.querySelectorAll('[data-evol-gift-progress]');
    if (nodes.length === 0) return;

    const currentTierN = tier ? tier.n : 0;
    const justUnlocked = lastTierN != null && currentTierN > lastTierN ? currentTierN : null;
    const justLost = lastTierN != null && currentTierN < lastTierN ? lastTierN : null;

    // Promote justLost into persistent module state the first time we see it.
    // Also auto-expire after LOSS_DURATION_MS. This survives Dawn's section swap.
    if (justLost) {
      lossState = { tierN: justLost, shownAt: Date.now() };
      // After expiry, re-render so the banner hides itself even if no other
      // cart event fires in the meantime.
      setTimeout(() => renderFromFetch(), LOSS_DURATION_MS + 100);
    } else if (lossState && Date.now() - lossState.shownAt > LOSS_DURATION_MS) {
      lossState = null;
    }

    nodes.forEach((el) => {
      if (cart.item_count === 0) {
        el.hidden = true;
        el.removeAttribute('data-evol-gift-tier');
        return;
      }
      el.hidden = false;
      el.setAttribute('data-evol-gift-tier', String(currentTierN));

      const pct = Math.min(100, Math.max(0, (gbpSubtotal / MAX_GBP) * 100));
      const fillEl = el.querySelector('[data-evol-gift-fill]');
      if (fillEl) fillEl.style.width = pct + '%';

      const track = el.querySelector('.evol-gift-progress__track');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));

      el.querySelectorAll('[data-evol-gift-notch]').forEach((n) => {
        const idx = parseInt(n.dataset.evolGiftNotch, 10) - 1;
        const notchTierN = idx + 1;
        const thresholdGbp = CONFIG.thresholdsGbp[idx];
        const isUnlocked = gbpSubtotal >= thresholdGbp;
        n.dataset.unlocked = isUnlocked ? 'true' : 'false';
        n.dataset.current = isUnlocked && notchTierN === currentTierN ? 'true' : 'false';
        n.style.left = ((thresholdGbp / MAX_GBP) * 100).toFixed(1) + '%';

        if (justUnlocked && notchTierN === justUnlocked) {
          n.setAttribute('data-just-unlocked', 'true');
          setTimeout(() => n.removeAttribute('data-just-unlocked'), 800);
        }
      });

      // Loss-framing banner: renders whenever lossState is live. Survives Dawn's
      // section HTML swap because lossState is module-level, not DOM-level.
      const lossEl = el.querySelector('[data-evol-gift-loss]');
      if (lossEl) {
        if (lossState) {
          const nextTier = CONFIG.tiers.find((t) => t.minGbp > gbpSubtotal);
          const remainCents = nextTier ? gbpToPresentmentCents(nextTier.minGbp - gbpSubtotal) : 0;
          const lostReward = TIER_REWARD_DESC[lossState.tierN] || 'your gifts';
          const remainStr = remainCents > 0 ? formatPresentment(remainCents, cart.currency) : '';
          lossEl.innerHTML = '<strong>You just lost Tier ' + lossState.tierN + ':</strong> ' + lostReward
            + (remainStr ? '. Add ' + remainStr + ' to get them back.' : '');
          lossEl.hidden = false;
        } else {
          lossEl.hidden = true;
          lossEl.innerHTML = '';
        }
      }

      const statusEl = el.querySelector('[data-evol-gift-status]');
      const helperEl = el.querySelector('[data-evol-gift-helper]');
      if (tier) {
        if (statusEl) statusEl.textContent = 'Tier ' + tier.n + ' unlocked';
        const next = CONFIG.tiers.find((t) => t.minGbp > gbpSubtotal);
        if (helperEl) {
          if (next) {
            const remainCents = gbpToPresentmentCents(next.minGbp - gbpSubtotal);
            helperEl.textContent =
              'Add ' + formatPresentment(remainCents, cart.currency) + ' ' + NEXT_TIER_COPY[next.n];
          } else {
            helperEl.textContent = 'Maximum tier unlocked. Enjoy the extras';
          }
        }
      } else {
        if (statusEl) statusEl.textContent = 'No gift yet';
        const first = CONFIG.tiers[0];
        if (helperEl) {
          const remainCents = gbpToPresentmentCents(first.minGbp - gbpSubtotal);
          helperEl.textContent = 'Add ' + formatPresentment(remainCents, cart.currency) + ' ' + NEXT_TIER_COPY[1];
        }
      }
    });

    lastTierN = currentTierN;
  }

  async function fetchJson(url, opts) {
    // Retry transient errors (429 rate-limit, 502/503 transient backend) with
    // exponential backoff. Cart reconciler fires multiple sequential calls per
    // tier transition; any one rate-limited drop would leave the cart in a
    // partial state. 3 attempts at 250 / 500 / 1000 ms covers Shopify's typical
    // rate-limit recovery window without making the user wait too long.
    const maxAttempts = 3;
    let lastErr;
    for (let i = 0; i < maxAttempts; i++) {
      let resp;
      try {
        resp = await fetch(url, opts);
      } catch (e) {
        lastErr = e;
        if (i === maxAttempts - 1) throw e;
        await new Promise((r) => setTimeout(r, 250 * Math.pow(2, i)));
        continue;
      }
      if (resp.ok) return resp.json();
      if ((resp.status === 429 || resp.status === 502 || resp.status === 503) && i < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, 250 * Math.pow(2, i)));
        continue;
      }
      throw new Error('HTTP ' + resp.status);
    }
    throw lastErr || new Error('fetchJson exhausted retries');
  }

  async function addGift(variantId, quantity, tierN) {
    return fetchJson('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: {
          _gift_tier: String(tierN),
          _auto_added: 'true',
        },
      }),
    });
  }

  async function changeGiftQty(key, quantity) {
    return fetchJson('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
  }

  async function batchUpdate(updates) {
    // /cart/update.js applies all updates atomically in a single round-trip.
    // Key can be a line-item key or variant_id. Used for tamper cleanup + qty changes
    // on already-existing lines. Does not accept line-item properties, so new adds
    // (with _auto_added / _gift_tier) still go through addGift.
    if (Object.keys(updates).length === 0) return null;
    return fetchJson('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });
  }

  function gbpFromCart(cart) {
    let cents = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
    // The Reel Reward Pack never counts toward gift tiers: the checkout validator excludes it, so counting its £6.99
    // here put £194-£195 carts on a tier checkout then refused ("more free gifts than your order total qualifies for").
    (cart.items || []).forEach(function (it) { if (it.variant_id === REEL_ID) cents -= it.final_line_price || 0; });
    return presentmentCentsToGbp(cents);
  }

  async function refreshDawnCartSections() {
    const ci = document.querySelector('cart-items');
    const cdi = document.querySelector('cart-drawer-items');
    const tasks = [];
    if (ci && typeof ci.onCartUpdate === 'function') tasks.push(ci.onCartUpdate());
    if (cdi && typeof cdi.onCartUpdate === 'function') tasks.push(cdi.onCartUpdate());
    // cart-icon-bubble + cart-notification-button don't listen to cartUpdate events;
    // refresh their HTML directly so the header count + "View cart (N)" stay in sync.
    tasks.push(refreshSectionsHtml(['cart-icon-bubble', 'cart-notification-button']));
    await Promise.all(tasks);
  }

  async function refreshSectionsHtml(ids) {
    try {
      const resp = await fetch('/?sections=' + ids.join(','), { headers: { Accept: 'application/json' } });
      if (!resp.ok) return;
      const sections = await resp.json();
      ids.forEach((id) => {
        const html = sections[id];
        const target = document.getElementById(id);
        if (!html || !target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = doc.querySelector('.shopify-section') || doc.body;
        const innerRoot = container.querySelector('#' + id) || container;
        target.innerHTML = innerRoot.innerHTML;
      });
    } catch (e) {
      /* non-fatal */
    }
  }

  // Bounded retry counter for partial-failure auto-recovery — prevents an
  // infinite reconcile loop if the function keeps rejecting (e.g. a cart is
  // genuinely in a state the function won't accept and never will).
  let reconcileRetryCount = 0;
  const MAX_RECONCILE_RETRIES = 2;

  async function reconcile() {
    if (reconciling) return;
    reconciling = true;
    let failures = 0;
    try {
      let cart = await fetchJson('/cart.js');
      let gbp = gbpFromCart(cart);
      let tier = determineTier(gbp);

      // Optimistic bar render from current cart — paint before any mutations.
      renderBar(cart, gbp, tier);

      // Build a single update map: tampered-line removals + qty adjustments
      // on existing (legit) gift lines. Applied atomically via /cart/update.js.
      // Net-new adds (have=0, want>0) still go through addGift because /cart/update.js
      // can't attach line-item properties.
      const updates = {};
      const newAdds = [];

      const tampered = findTamperedGiftLines(cart);
      tampered.forEach((t) => { updates[t.key] = 0; });

      const current = currentGiftLines(cart);
      const desired = desiredGiftLines(tier);
      GIFT_IDS.forEach((vid) => {
        const want = desired[vid] || 0;
        const have = current[vid] ? current[vid].quantity : 0;
        if (want === have) return;
        if (have === 0 && want > 0) {
          newAdds.push({ vid: vid, qty: want });
        } else if (have > 0 && want === 0) {
          updates[current[vid].key] = 0;
        } else {
          updates[current[vid].key] = want;
        }
      });

      const didWork = Object.keys(updates).length > 0 || newAdds.length > 0;

      // Per-operation try/catch — a failure in one (e.g. cart-validation
      // function rejected, or transient 429 after exhausted retries) doesn't
      // abort subsequent operations. We log + count failures and trigger an
      // auto-retry below if any didn't land.
      if (Object.keys(updates).length > 0) {
        try {
          await batchUpdate(updates);
        } catch (e) {
          failures++;
          console.warn('[evol-gift] batchUpdate failed', e);
        }
      }

      for (const a of newAdds) {
        try {
          await addGift(a.vid, a.qty, tier.n);
        } catch (e) {
          failures++;
          console.warn('[evol-gift] addGift failed', a.vid, e);
        }
      }

      if (didWork) {
        await refreshDawnCartSections().catch(() => {});
        try {
          const fresh = await fetchJson('/cart.js');
          const freshGbp = gbpFromCart(fresh);
          renderBar(fresh, freshGbp, determineTier(freshGbp));
        } catch (e) {
          // Render failure is non-fatal — bar will be repainted on next event.
        }
      }
    } catch (err) {
      console.warn('[evol-gift] reconcile failed', err);
      failures++;
    } finally {
      reconciling = false;
    }

    // If any operation failed, schedule one more reconcile attempt to fix
    // the partial state. The counter only resets after a fully clean pass —
    // decrementing it when the retry fires (the old behaviour) meant the cap
    // never accumulated, so sustained failure (e.g. a 429) retried forever,
    // and the request volume kept the session rate-limited: checkout dead.
    if (failures === 0) {
      reconcileRetryCount = 0;
    } else if (reconcileRetryCount < MAX_RECONCILE_RETRIES) {
      reconcileRetryCount++;
      setTimeout(() => {
        reconcile();
      }, 2000 * reconcileRetryCount);
    }
  }

  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  async function renderFromFetch() {
    try {
      const cart = await fetchJson('/cart.js');
      const gbp = gbpFromCart(cart);
      renderBar(cart, gbp, determineTier(gbp));
      renderPdpBanner(cart, gbp);
      renderReelNote(cart);
    } catch (err) {
      /* ignore */
    }
  }

  // Per-tier reward description used by both the cart bar and the PDP banner.
  const TIER_REWARD_DESC = {
    1: '1 Mystery Pack',
    2: '2 Mystery Packs',
    3: '4 Mystery Packs + Ultra Pro sleeves',
    4: '8 Mystery Packs + 2 sleeves packs',
  };

  // Benefit-first PDP copy (owner, 1 Oct): the reward, not the tier number. "Adding this
  // unlocks Tier 2: 2 Mystery Packs free" put two 2s side by side, and a first-time visitor
  // has no idea what Tier 2 is. The cart's progress bar keeps its Tier labels (they match
  // its notches), and "Every order gets a free gift" stays (owner's call).
  const PDP_UNLOCK_DESC = {
    1: 'a free Mystery Pack',
    2: '2 free Mystery Packs',
    3: '4 free Mystery Packs + Ultra Pro sleeves',
    4: '8 free Mystery Packs + 2 packs of sleeves',
  };

  function renderPdpBanner(cart, currentGbp) {
    const banners = document.querySelectorAll('[data-evol-gift-pdp]');
    if (banners.length === 0) return;
    banners.forEach((a) => {
      const priceCents = parseFloat(a.getAttribute('data-product-price'));
      const textEl = a.querySelector('[data-evol-gift-pdp-text]');
      if (!Number.isFinite(priceCents) || !textEl) return;
      const productGbp = presentmentCentsToGbp(priceCents);
      const afterGbp = currentGbp + productGbp;
      const currentTier = determineTier(currentGbp);
      const afterTier = determineTier(afterGbp);
      const currencyCode = cart.currency || 'GBP';

      let html;
      if (afterTier && afterTier.n === 4 && currentTier && currentTier.n === 4) {
        html = "<strong>You're at Tier 4.</strong> Enjoy the extras";
      } else if (afterTier && (!currentTier || afterTier.n > currentTier.n)) {
        html = '<strong>Adding this unlocks</strong> ' + PDP_UNLOCK_DESC[afterTier.n];
      } else if (afterTier && currentTier && afterTier.n === currentTier.n) {
        const nextTier = CONFIG.tiers.find((t) => t.minGbp > afterGbp);
        if (nextTier) {
          const remainCents = gbpToPresentmentCents(nextTier.minGbp - afterGbp);
          html = 'Add <strong>' + formatPresentment(remainCents, currencyCode) + '</strong> more for ' + PDP_UNLOCK_DESC[nextTier.n];
        } else {
          html = "<strong>You're at Tier 4.</strong> Enjoy the extras";
        }
      } else {
        const firstTier = CONFIG.tiers[0];
        const remainCents = gbpToPresentmentCents(firstTier.minGbp - afterGbp);
        if (remainCents > 0) {
          if (currentGbp === 0) {
            // Empty cart on a sub-Tier-1 product: the "£X more" maths reads as
            // a condition on this purchase — suppress the banner until the cart
            // has value (Liquid pre-hides it for sub-£50 products to avoid a
            // fallback-text flash before this runs).
            a.style.display = 'none';
            return;
          }
          html = 'Every order gets a free gift. Add <strong>' + formatPresentment(remainCents, currencyCode) + '</strong> more to unlock your first Mystery Pack';
        } else {
          html = '<strong>Adding this unlocks</strong> ' + PDP_UNLOCK_DESC[1];
        }
      }
      a.style.display = '';
      textEl.innerHTML = html;
    });
  }

  /* -----------------------------------------------------------------
     Dawn quantity-update preflight: reduce gifts BEFORE the user's
     paid-line decrease commits. Without this the cart-validation
     Function rejects the change because the post-change state has
     more gifts than the new tier permits, leaving the user stuck
     with a "more free gifts than your current order total qualifies
     for" error and no way to recover except manual gift removal.

     Strategy: monkey-patch updateQuantity on cart-items and
     cart-drawer-items custom elements. When the user reduces a PAID
     line (not a gift, qty going down), compute the proposed
     non-gift subtotal, derive the new tier, and atomically reduce
     any gift lines that exceed the new tier's max — all before the
     original updateQuantity fires its /cart/change.js call.

     Increases need no preflight: gifts stay <= old-tier-max which
     is also <= new-tier-max, function accepts. Reactive reconciler
     then fires post-update to add any newly-unlocked gifts.
     ----------------------------------------------------------------- */
  function patchCartQuantityForGiftPreflight() {
    function patchCtor(typeName) {
      const ctor = customElements.get(typeName);
      if (!ctor || !ctor.prototype) return false;
      const current = ctor.prototype.updateQuantity;
      if (typeof current !== 'function') return false;
      // Marker lives on the FUNCTION, not the prototype, so if a Shopify app
      // or section refresh replaces the function we re-wrap the new one
      // (the prototype-level marker would survive even after the function
      // was swapped, leaving us un-patched but flagged as patched).
      if (current._evolPatchedFn === true) return true;
      const original = current;

      const patched = async function (line, quantity, event, name, variantId) {
        // Dawn passes `line` as a string (from `event.target.dataset.index`)
        // for cart-page +/- buttons and the trash icon. Coerce for numeric
        // comparisons below.
        const lineIdx = parseInt(line, 10);
        const newQty = parseInt(quantity, 10);

        let cart;
        try {
          cart = await fetchJson('/cart.js');
        } catch (e) {
          return original.call(this, line, quantity, event, name, variantId);
        }

        const targetItem = Number.isFinite(lineIdx) ? cart.items[lineIdx - 1] : null;
        const targetIsGift = targetItem && GIFT_IDS.has(targetItem.variant_id);
        const isReduction = targetItem && Number.isFinite(newQty) && newQty < targetItem.quantity;

        // Only intercept reductions of PAID lines (incl. trash → qty 0). Gift
        // lines and paid increases pass through unmodified — original handles.
        if (!targetItem || targetIsGift || !isReduction) {
          return original.call(this, line, quantity, event, name, variantId);
        }

        // Compute proposed non-gift subtotal (presentment cents) AFTER the change.
        let proposedNonGiftCents = 0;
        cart.items.forEach((it, idx) => {
          if (GIFT_IDS.has(it.variant_id)) return;
          const lineNum = idx + 1;
          if (lineNum === lineIdx) {
            proposedNonGiftCents += (it.final_price || it.price || 0) * newQty;
          } else {
            proposedNonGiftCents += it.final_line_price != null ? it.final_line_price : (it.line_price || 0);
          }
        });
        const proposedGbp = presentmentCentsToGbp(proposedNonGiftCents);
        const proposedTier = determineTier(proposedGbp);
        const desired = desiredGiftLines(proposedTier);

        // Build atomic update map keyed by line-item KEY (stable across cart
        // mutations — line indices shift when gift lines are removed). The
        // map combines the user's intended paid-line change AND any gift
        // line reductions required by the new tier. Single round-trip means
        // the cart-validation function only ever sees a consistent state.
        const updates = {};
        updates[targetItem.key] = newQty;
        cart.items.forEach((it) => {
          if (!GIFT_IDS.has(it.variant_id)) return;
          const want = desired[it.variant_id] || 0;
          if (it.quantity > want) {
            updates[it.key] = want;
          }
        });

        try {
          if (typeof this.enableLoading === 'function') this.enableLoading(line);
          const sectionsList =
            typeof this.getSectionsToRender === 'function' ? this.getSectionsToRender() : [];
          const sectionNames = sectionsList.map((s) => s.section);

          const resp = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              updates: updates,
              sections: sectionNames,
              sections_url: window.location.pathname,
            }),
          });
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          const parsedState = await resp.json();

          // Mirror Dawn's section-refresh + empty-state toggling so the cart
          // UI stays in sync without needing a follow-up onCartUpdate fetch.
          if (this.classList) this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          sectionsList.forEach((section) => {
            const target = document.getElementById(section.id);
            if (!target) return;
            const elementToReplace = target.querySelector(section.selector) || target;
            const html = parsedState.sections && parsedState.sections[section.section];
            if (!html) return;
            elementToReplace.innerHTML =
              typeof this.getSectionInnerHTML === 'function'
                ? this.getSectionInnerHTML(html, section.selector)
                : html;
          });

          // Re-render the tier bar from the atomic-update result. The
          // subscribe handler skips events with source=SOURCE (so it doesn't
          // re-trigger reconcile), which means it ALSO skips its own
          // renderBar call. Doing it manually here ensures the tier UI
          // (progress bar, "Tier N unlocked", helper copy) updates after a
          // qty reduction even though we're suppressing the reconcile.
          try {
            const newGbp = gbpFromCart(parsedState);
            renderBar(parsedState, newGbp, determineTier(newGbp));
          } catch (e) {
            /* non-fatal */
          }

          // Publish cartUpdate so other listeners (loyalty widgets, third-party
          // apps) re-render. Source tag matches the reconciler's SOURCE so
          // its subscribe handler skips re-running — the atomic update
          // already reconciled.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: SOURCE,
              cartData: parsedState,
              variantId: variantId,
            });
          }

          if (typeof this.disableLoading === 'function') this.disableLoading(line);
          return parsedState;
        } catch (err) {
          console.warn('[evol-gift] atomic update failed, falling through to Dawn', err);
          if (typeof this.disableLoading === 'function') this.disableLoading(line);
          return original.call(this, line, quantity, event, name, variantId);
        }
      };
      patched._evolPatchedFn = true;
      ctor.prototype.updateQuantity = patched;
      return true;
    }

    function tryPatch() {
      const a = patchCtor('cart-items');
      const b = patchCtor('cart-drawer-items');
      return a || b;
    }

    // Custom elements may be registered after this script runs. Retry briefly.
    if (!tryPatch()) {
      let attempts = 0;
      const t = setInterval(() => {
        if (tryPatch() || ++attempts >= 15) clearInterval(t);
      }, 200);
    }
  }

  /* -----------------------------------------------------------------
     Dawn's cart-items.onCartUpdate replaces the entire cart-items
     innerHTML with server-rendered HTML — wiping every JS-set value
     in the tier-progress bar (status text, helper text, fill width,
     notch states, loss banner). Without intervention the bar shows
     skeleton-only for ~6 seconds after every cart change until an
     unrelated setTimeout in renderBar fires renderFromFetch.

     Patch onCartUpdate to renderBar AFTER the section refresh
     completes. Function-level marker + re-assert on cartUpdate
     mirrors the updateQuantity defensive pattern.
     ----------------------------------------------------------------- */
  function patchOnCartUpdateForBarRepaint() {
    function patchCtor(typeName) {
      const ctor = customElements.get(typeName);
      if (!ctor || !ctor.prototype) return false;
      const current = ctor.prototype.onCartUpdate;
      if (typeof current !== 'function') return false;
      if (current._evolBarRepaintPatched === true) return true;
      const original = current;
      const patched = async function () {
        const result = await original.call(this);
        // Section refresh complete — repaint the tier bar.
        try {
          await renderFromFetch();
        } catch (err) {
          /* non-fatal */
        }
        return result;
      };
      patched._evolBarRepaintPatched = true;
      ctor.prototype.onCartUpdate = patched;
      return true;
    }

    function tryPatch() {
      const a = patchCtor('cart-items');
      const b = patchCtor('cart-drawer-items');
      return a || b;
    }

    if (!tryPatch()) {
      let attempts = 0;
      const t = setInterval(() => {
        if (tryPatch() || ++attempts >= 15) clearInterval(t);
      }, 200);
    }
  }

  function init() {
    // On the /cart page, run reconcile immediately (no debounce) — any tampered
    // gift lines must be removed before the user can see the tampered total.
    // On other pages, debounce is fine: cart events rapid-fire during ATC and
    // we only need to reconcile once at the end of the burst.
    const onCartPage = /^\/cart(\/|\?|$)/.test(window.location.pathname + window.location.search);
    if (onCartPage) {
      renderFromFetch().finally(reconcile);
    } else {
      renderFromFetch().finally(scheduleReconcile);
    }

    patchCartQuantityForGiftPreflight();
    patchOnCartUpdateForBarRepaint();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Re-assert both prototype patches — Shopify apps that redefine
        // CartItems.updateQuantity / onCartUpdate (or replaceWith on the
        // element with a fresh class instance) can silently undo our
        // wrappers. Cheap to re-run; no-op if already patched.
        patchCartQuantityForGiftPreflight();
        patchOnCartUpdateForBarRepaint();
        if (event && event.source === SOURCE) return;
        if (event && event.cartData && event.cartData.items) {
          const gbp = gbpFromCart(event.cartData);
          renderBar(event.cartData, gbp, determineTier(gbp));
          renderReelNote(event.cartData);
        } else {
          renderFromFetch();
        }
        scheduleReconcile();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =========================================================
   Chase-card lightbox (blog articles)
   Click any .evol-chase-card to open the full-size image.
   ========================================================= */
(function () {
  function buildLightbox() {
    if (document.getElementById('evol-lightbox')) return document.getElementById('evol-lightbox');
    var box = document.createElement('div');
    box.id = 'evol-lightbox';
    box.className = 'evol-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML =
      '<button type="button" class="evol-lightbox__close" aria-label="Close">×</button>' +
      '<img class="evol-lightbox__img" alt="" />' +
      '<div class="evol-lightbox__caption" aria-live="polite"></div>';
    document.body.appendChild(box);
    return box;
  }

  function open(src, alt, caption) {
    var box = buildLightbox();
    var img = box.querySelector('.evol-lightbox__img');
    var cap = box.querySelector('.evol-lightbox__caption');
    img.src = src;
    img.alt = alt || '';
    cap.textContent = caption || alt || '';
    cap.style.display = caption ? '' : 'none';
    box.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    var box = document.getElementById('evol-lightbox');
    if (!box) return;
    box.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    var img = box.querySelector('.evol-lightbox__img');
    if (img) img.src = '';
  }

  document.addEventListener('click', function (ev) {
    var card = ev.target.closest && ev.target.closest('.evol-chase-card');
    if (card) {
      ev.preventDefault();
      var img = card.querySelector('img');
      var fullSrc = card.getAttribute('data-full') || (img && img.src);
      var caption = card.getAttribute('data-caption') || (img && img.alt) || '';
      open(fullSrc, img && img.alt, caption);
      return;
    }
    var box = document.getElementById('evol-lightbox');
    if (!box || box.getAttribute('aria-hidden') === 'true') return;
    if (ev.target === box || (ev.target.classList && ev.target.classList.contains('evol-lightbox__close'))) {
      close();
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape') close();
  });
})();

/* Header-drawer dropdown accordion (Sealed, Tools, any parent item) — overrides Dawn's
   default details toggle for smooth height expansion animated to the exact content height */
(function () {
  function ready(fn) { document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn); }
  ready(function () {
    var summaries = document.querySelectorAll('summary[id^="HeaderDrawer-"]');
    summaries.forEach(function (summary) {
      var details = summary.parentElement;
      var submenu = details.querySelector('.menu-drawer__submenu.has-submenu');
      if (!details || !submenu) return;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (details.hasAttribute('open')) {
          // CLOSE
          var current = submenu.scrollHeight;
          submenu.style.setProperty('max-height', current + 'px', 'important');
          submenu.offsetHeight;
          submenu.style.setProperty('max-height', '0px', 'important');
          submenu.addEventListener('transitionend', function te() {
            details.removeAttribute('open');
            submenu.removeEventListener('transitionend', te);
          }, { once: true });
        } else {
          // OPEN
          details.setAttribute('open', '');
          submenu.style.setProperty('max-height', '0px', 'important');
          submenu.offsetHeight;
          var target = submenu.scrollHeight;
          submenu.style.setProperty('max-height', target + 'px', 'important');
          submenu.addEventListener('transitionend', function te() {
            submenu.style.setProperty('max-height', target + 'px', 'important');
            submenu.removeEventListener('transitionend', te);
          }, { once: true });
        }
      });
    });
  });
})();

/* =========================================================
   Vault Reveal popup — "case-opening" reel (v2)
   - Behaviour: snippets/evol-reel-config.liquid (#evol-reel-config)
   - Outcome:   ALWAYS lands on the single gift tile ([data-reel-win]). It is a
     gift available to all, NOT a prize draw — there are no odds and no weighting.
     The premium box/ETB tiles are display-only hype and are never targeted.
   - Flow: SPIN → REVEAL → EMAIL → PHONE (optional) → DONE
   - Production consent = the Omnisend embed in the email step; the demo
     fields below are preview-only (client identify does NOT record consent).
   ========================================================= */
(function () {
  const root = document.querySelector('[data-evol-reel]');
  const cfgEl = document.getElementById('evol-reel-config');
  if (!root || !cfgEl) return;

  let CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!CFG.enabled) return;

  const params = new URLSearchParams(window.location.search);
  const FORCE = !!(CFG.forceParam && params.get(CFG.forceParam) === '1');

  const STORE_KEY = 'evol-reel-state';
  const DAY = 86400000;
  const NOW = Date.now();

  const readState = function () {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
  };
  const writeState = function (s) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {}
  };

  const dialog = root.querySelector('.evol-reel__dialog');
  const overlay = root.querySelector('.evol-reel__overlay');
  const track = root.querySelector('[data-reel-track]');
  const spinBtn = root.querySelector('[data-reel-spin]');
  const steps = {};
  root.querySelectorAll('[data-reel-step]').forEach(function (el) {
    steps[el.getAttribute('data-reel-step')] = el;
  });
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reopenBtn = document.querySelector('[data-reel-reopen]');
  // Funnel events. window.gtag here is a no-op stub, so they go through Shopify.analytics;
  // the "Evol GA4 custom events" pixel forwards them (docs/ga4-custom-pixel.md).
  const emit = function (name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, data || {});
    } catch (e) {}
  };
  let openMode = 'auto';
  // Re-reads the Omnisend form's step into the reveal classes (set up with the embed below).
  let formSync = null;
  // Where this sign-up came from, sent with the "popup signup" Omnisend event: the tool's name
  // from a card's data-evol-source, 'alerts' for other drop-alert CTAs, 'popup' otherwise.
  let signupSource = 'popup';
  // Never on the quiet tool pages (autoOpen false): the pill covered calculator controls at 375px.
  const showReopen = function () { if (reopenBtn && CFG.autoOpen !== false) reopenBtn.hidden = false; };

  // ---- Reel strip: shuffle the product pool + duplicate for a seamless one-way loop ----
  const shuffle = function (a) {
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = a[i]; a[i] = a[j]; a[j] = t; }
    return a;
  };
  let POOL = null;
  const buildStrip = function () {
    if (!track) return;
    if (!POOL) {
      POOL = Array.prototype.slice.call(track.children).map(function (n) { return n.cloneNode(true); });
      // Warm the image cache once so the reel never pops a late image in mid-loop.
      const seen = {};
      POOL.forEach(function (n) {
        const im = n.querySelector('img');
        if (im && im.src && !seen[im.src]) { seen[im.src] = 1; const pre = new Image(); pre.src = im.src; }
      });
    }
    const decoys = shuffle(POOL.filter(function (t) { return !t.hasAttribute('data-reel-win'); }).map(function (n) { return n.cloneNode(true); }));
    const winT = POOL.filter(function (t) { return t.hasAttribute('data-reel-win'); })[0];
    if (!winT) return;
    // Packs stay frequent (~1 in 3 tiles) so landing on one looks natural, but two
    // pack tiles are never adjacent — identical tiles side by side read as a broken
    // reel. Pattern: win, then 2 decoys (30% chance of a 3rd for organic spacing).
    const COPY_LEN = 24;
    const seq = [];
    let di = 0;
    while (seq.length < COPY_LEN) {
      seq.push(winT.cloneNode(true));
      if (!decoys.length) continue;
      let gap = 2 + (Math.random() < 0.3 ? 1 : 0);
      while (gap-- > 0 && seq.length < COPY_LEN) {
        seq.push(decoys[di % decoys.length].cloneNode(true));
        di++;
      }
    }
    // The strip renders as copy1 + copy2 and every copy STARTS with a win tile, so a
    // copy that also ENDS with one puts two wins adjacent across the loop seam.
    // Swap a trailing win for a decoy to keep the no-adjacent guarantee seam-safe.
    if (decoys.length && seq[seq.length - 1].hasAttribute('data-reel-win')) {
      seq[seq.length - 1] = decoys[di % decoys.length].cloneNode(true);
      di++;
    }
    const frag = document.createDocumentFragment();
    seq.forEach(function (n) { frag.appendChild(n.cloneNode(true)); }); // copy 1
    seq.forEach(function (n) { frag.appendChild(n.cloneNode(true)); }); // copy 2 (identical → seamless loop)
    track.innerHTML = '';
    track.appendChild(frag);
    track.style.transition = 'none';
    track.style.transform = '';
    track.style.animation = ''; // use the CSS drift
    // Build the whole line, then HOLD the drift paused until every tile is fully
    // DECODED — not merely loaded. A loaded-but-undecoded image decodes on its first
    // paint, i.e. the moment it drifts into the masked viewport, and that first-paint
    // decode IS the visible "pop-in". img.decode() forces the decode up front so the
    // loop only ever scrolls already-painted tiles. (copy 1 + copy 2 share srcs, so
    // decoding each image warms both.) decoding="sync" stops the browser re-decoding
    // asynchronously at display size, which would re-introduce the flash mid-scroll.
    const imgs = Array.prototype.slice.call(track.querySelectorAll('img'));
    imgs.forEach(function (im) { im.loading = 'eager'; im.decoding = 'sync'; });
    track.style.animationPlayState = 'paused';
    let started = false;
    const startDrift = function () { if (started) return; started = true; track.style.animationPlayState = 'running'; };
    const loaded = function (im) {
      if (im.complete && im.naturalWidth > 0) return Promise.resolve();
      return new Promise(function (res) { im.addEventListener('load', res, { once: true }); im.addEventListener('error', res, { once: true }); });
    };
    const waits = imgs.map(function (im) {
      return loaded(im).then(function () {
        return (typeof im.decode === 'function') ? im.decode().catch(function () {}) : null;
      });
    });
    Promise.all(waits).then(startDrift);
    setTimeout(startDrift, 3000); // fallback if an image hangs or never decodes
  };

  // Warm the reel before the auto-open: fetch its images while the visitor reads the page, so
  // the reel drifts the moment the popup appears (on a slow phone it sat still ~2.7s while
  // they downloaded). Only runs for visitors who are about to get the popup.
  let warmed = false;
  const warmReel = function () {
    if (warmed || !track) return;
    warmed = true;
    const seenSrc = {};
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (im) {
      const src = im.getAttribute('src');
      if (!src || seenSrc[src]) return;
      seenSrc[src] = 1;
      const pre = new Image();
      pre.decoding = 'async';
      pre.src = src;
    });
    const pack = root.querySelector('.evol-reel__won-media img');
    if (pack) pack.loading = 'eager';
  };

  let opened = false;
  let spun = false;
  let lastFocus = null;
  let capturedEmail = '';
  let delayTimer = null;
  let scrollHandler = null;
  let exitHandler = null;

  const teardownTriggers = function () {
    if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }
    if (scrollHandler) { window.removeEventListener('scroll', scrollHandler); scrollHandler = null; }
    if (exitHandler) { document.removeEventListener('mouseout', exitHandler); exitHandler = null; }
  };

  const showStep = function (name) {
    Object.keys(steps).forEach(function (k) { steps[k].hidden = (k !== name); });
    if (name === 'reveal' && formSync) formSync(); // classes can be stale from before the popup closed
    const focusable = steps[name] && steps[name].querySelector('input, button, a[href]');
    if (focusable && name !== 'spin') focusable.focus();
  };

  const open = function (skipStrip) {
    if (opened) return;
    opened = true;
    teardownTriggers();
    lastFocus = document.activeElement;
    const st = readState();
    // One spin per visitor: once the reel has landed (revealedAt), every later entry —
    // reopen widget, auto-trigger, ?reel=1 — skips the spin theatre and goes straight
    // to the claim form ("Your gift is waiting"). Re-running the animation would expose
    // the always-lands-on-the-gift mechanic and cheapen the one-time reveal.
    const alreadyRevealed = !!st.revealedAt;
    const packImg = root.querySelector('.evol-reel__won-media img');
    if (packImg) packImg.loading = 'eager';
    if (!skipStrip && !alreadyRevealed) buildStrip(); // fresh randomised reel each open
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    void root.offsetWidth;
    root.classList.add('is-open');
    document.body.classList.add('evol-reel-open');
    st.seenAt = NOW;
    writeState(st);
    if (alreadyRevealed) {
      root.classList.add('is-revealed-return');
      showStep('reveal');
    }
    if (dialog) dialog.focus();
    emit('evol_reel_open', { mode: openMode === 'auto' && alreadyRevealed ? 'return' : openMode, source: signupSource });
    openMode = 'auto';
  };

  // Alerts mode — CTA-triggered entry ([data-evol-alerts] anywhere on the page).
  // Same modal + same Omnisend embed, but skips the spin/reel straight to the form,
  // with drop-alerts copy swapped in via .is-alerts-mode (CSS). Explicit clicks
  // bypass frequency gating entirely (user intent beats "seen recently").
  const openAlerts = function (source) {
    if (opened) return;
    signupSource = source || 'alerts';
    root.classList.add('is-alerts-mode');
    if (dialog) dialog.setAttribute('aria-label', 'Drop alerts');
    // CSS shows the alerts label on the form's first button; give screen readers the same words.
    const alertsBtn = root.querySelector('.evol-reel__embed [class*="-step-0-container"] button');
    if (alertsBtn && root.dataset.alertsCta) alertsBtn.setAttribute('aria-label', root.dataset.alertsCta);
    openMode = 'alerts';
    open(true); // skip the reel build — the spin step is never shown in this mode
    showStep('reveal');
    // Prime the frame-sync while the email input is measurably visible, so the alerts
    // heading reliably hides once the embed advances past the email step.
    try { window.dispatchEvent(new CustomEvent('omnisendForms')); } catch (e) {}
  };

  const stepNow = function () {
    const rv = steps.reveal;
    if (rv && !rv.hidden) return rv.classList.contains('is-form-done') ? 'success' : (rv.classList.contains('is-phone-step') ? 'phone' : 'email');
    if (steps.done && !steps.done.hidden) return 'success';
    return 'spin';
  };

  const close = function () {
    if (!opened) return;
    emit('evol_reel_close', { step: stepNow() });
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('evol-reel-open');
    let hidden = false;
    const hide = function () {
      if (hidden) return;
      hidden = true;
      root.hidden = true;
      // Reset to a fresh spin so the re-entry widget reopens cleanly.
      opened = false;
      spun = false;
      root.classList.remove('is-alerts-mode');
      root.classList.remove('is-revealed-return');
      signupSource = 'popup';
      phoneCountrySet = false;
      const alertsBtn = root.querySelector('.evol-reel__embed [class*="-step-0-container"] button[aria-label]');
      if (alertsBtn) alertsBtn.removeAttribute('aria-label');
      if (dialog) dialog.setAttribute('aria-label', 'Vault Reveal');
      if (steps.spin) steps.spin.classList.remove('is-spinning', 'is-ui-gone');
      if (spinBtn) spinBtn.disabled = false;
      showStep('spin');
      if (track) { track.style.transition = 'none'; track.style.transform = ''; track.style.animation = ''; }
      const st = readState();
      if (!st.claimedAt) showReopen();
    };
    if (overlay) overlay.addEventListener('transitionend', hide, { once: true });
    setTimeout(hide, 450);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  // Current translateX of the (CSS-drifting) track.
  const currentX = function () {
    const tr = window.getComputedStyle(track).transform;
    if (!tr || tr === 'none') return 0;
    try { return new DOMMatrix(tr).m41; } catch (e) { return 0; }
  };

  const reveal = function () {
    // The reel has landed — bank it. From now on this browser never re-spins
    // (see open()); re-entries go straight to the claim form.
    try {
      const stR = readState();
      if (!stR.revealedAt) { stR.revealedAt = Date.now(); writeState(stR); }
    } catch (e) {}
    // Smooth hand-off: fade the spin step out, then fade the reveal step in (instead
    // of an abrupt cut after the pack lands).
    const spinStep = steps.spin;
    if (spinStep && !REDUCED) {
      spinStep.style.transition = 'opacity 0.16s ease';
      spinStep.style.opacity = '0';
      setTimeout(function () {
        spinStep.style.opacity = '';
        spinStep.style.transition = '';
        showStep('reveal');
      }, 160);
    } else {
      showStep('reveal');
    }
  };

  const spin = function () {
    if (spun || !track) return;
    spun = true;
    emit('evol_reel_spin');
    if (spinBtn) spinBtn.disabled = true;
    if (steps.spin) {
      steps.spin.classList.add('is-spinning'); // fades the button + terms
      // ...then collapses them: the card eases down to the reel and the reel settles (CSS).
      setTimeout(function () { if (spun && steps.spin) steps.spin.classList.add('is-ui-gone'); }, REDUCED ? 0 : 140);
    }

    // Strip is two identical copies. Land on a RANDOM gift tile in copy 1, but shift
    // the destination one full copy further left so the reel always travels FORWARD
    // from wherever the passive drift is (never snaps back), with decoys still in front.
    const allTiles = Array.prototype.slice.call(track.children);
    const len = allTiles.length;
    const half = Math.floor(len / 2);
    const wins = allTiles.filter(function (t, i) { return t.hasAttribute('data-reel-win') && i < half; });
    const band = wins.filter(function (w) { const i = allTiles.indexOf(w); return i >= half * 0.45 && i <= half * 0.85; });
    const pickFrom = band.length ? band : wins;
    const target = pickFrom.length ? pickFrom[Math.floor(Math.random() * pickFrom.length)] : allTiles[Math.floor(half * 0.7)];
    if (!target) { reveal(); return; }

    const stage = track.parentElement;
    const oneCopy = track.scrollWidth / 2;
    const jitter = (Math.random() * 0.2 - 0.1) * target.offsetWidth;
    const dest = Math.round(stage.clientWidth / 2 - (target.offsetLeft + target.offsetWidth / 2) + jitter - oneCopy);

    // Hand off from the passive drift: pin current position, then animate.
    const pinned = currentX();
    track.style.animation = 'none';
    track.style.transition = 'none';
    track.style.transform = 'translateX(' + pinned + 'px)';
    void track.offsetWidth;

    if (REDUCED) {
      track.style.transform = 'translateX(' + dest + 'px)';
      reveal();
      return;
    }

    // Longer spin with a hard deceleration tail so the last couple of tiles crawl
    // past the marker — builds anticipation before it settles on the pack.
    track.style.transition = 'transform 6000ms cubic-bezier(0.12, 0.8, 0.25, 1)';
    let done = false;
    const finish = function (e) {
      if (e && e.propertyName && e.propertyName !== 'transform') return;
      if (done) return;
      done = true;
      track.removeEventListener('transitionend', finish);
      setTimeout(reveal, 500); // pause on the landed pack, then fade to reveal
    };
    track.addEventListener('transitionend', finish);
    setTimeout(finish, 6600);
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        track.style.transform = 'translateX(' + dest + 'px)';
      });
    });
  };

  // ---- Capture ----
  const normalizePhone = function (v) {
    let p = (v || '').replace(/[^\d+]/g, '');
    if (!p) return '';
    if (p.charAt(0) === '+') return p;
    if (p.charAt(0) === '0') return '+44' + p.slice(1);
    return '+' + p;
  };
  const omni = function (payload, track2) {
    try {
      window.omnisend = window.omnisend || [];
      if (payload && (payload.email || payload.phone)) window.omnisend.push(['identifyContact', payload]);
      if (track2) window.omnisend.push(['track', '$reelClaim', track2]);
    } catch (e) {}
  };
  const finishDone = function () {
    const st = readState();
    st.claimedAt = Date.now();
    writeState(st);
    showStep('done');
  };

  const emailForm = root.querySelector('[data-reel-email-form]');
  const phoneForm = root.querySelector('[data-reel-phone-form]');

  if (emailForm) {
    emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      const emailEl = emailForm.querySelector('input[name="email"]');
      const errEl = emailForm.querySelector('[data-reel-error]');
      const email = (emailEl && emailEl.value || '').trim();
      if (!/.+@.+\..+/.test(email)) { if (errEl) { errEl.textContent = 'Please enter a valid email address.'; errEl.hidden = false; } return; }
      if (errEl) errEl.hidden = true;
      capturedEmail = email;
      // Identify + fire the code-email automation now (so they get it even if they skip phone).
      omni({ email: email }, { reward: 'Free Mystery Pack', sms: false });
      showStep('phone');
    });
  }

  if (phoneForm) {
    phoneForm.addEventListener('submit', function (e) {
      e.preventDefault();
      const phoneEl = phoneForm.querySelector('input[name="phone"]');
      const consentEl = phoneForm.querySelector('input[name="sms_consent"]');
      const errEl = phoneForm.querySelector('[data-reel-error]');
      const phoneRaw = (phoneEl && phoneEl.value || '').trim();
      const phone = normalizePhone(phoneRaw);
      const hasPhone = phone.replace(/\D/g, '').length >= 8;
      const fail = function (msg) { if (errEl) { errEl.textContent = msg; errEl.hidden = false; } };
      if (!phoneRaw) return finishDone(); // empty = treat as skip
      if (!hasPhone) return fail('That mobile looks incomplete. Fix it or skip.');
      if (consentEl && !consentEl.checked) return fail('Tick the box so we can text you, or skip.');
      if (errEl) errEl.hidden = true;
      omni({ email: capturedEmail, phone: phone }, null); // bank the number on the same contact
      finishDone();
    });
  }

  const skipBtn = root.querySelector('[data-reel-skip]');
  if (skipBtn) skipBtn.addEventListener('click', finishDone);

  // Embed mode: skip the optional phone step. The email is already captured by the
  // Omnisend form's step 1, so just bank the claim and jump to the success step.
  const embedSkip = root.querySelector('[data-reel-embed-skip]');
  if (embedSkip) embedSkip.addEventListener('click', function () {
    if (dialog) dialog.classList.remove('is-dropdown-open');
    emit('evol_reel_phone_skip');
    finishDone();
  });

  // ---- Close + focus trap ----
  root.querySelectorAll('[data-reel-close]').forEach(function (el) {
    el.addEventListener('click', function (e) { e.preventDefault(); close(); });
  });
  document.addEventListener('keydown', function (e) {
    if (!opened) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab' && dialog) {
      // root, not dialog: the close button sits outside the card, in the screen corner.
      const f = root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
      // getClientRects, not offsetParent: offsetParent is null for the position:fixed close button.
      const visible = Array.prototype.filter.call(f, function (n) { return n.getClientRects().length > 0; });
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  if (spinBtn) spinBtn.addEventListener('click', spin);
  if (reopenBtn) reopenBtn.addEventListener('click', function () { reopenBtn.hidden = true; openMode = 'reopen'; open(); });

  // Drop-alert CTAs: any [data-evol-alerts] element opens the modal in alerts mode.
  // Delegated so it covers links anywhere (footer, 404, Next Drop) and stays armed
  // after teardownTriggers(). The links keep href="/pages/notify" as the no-JS /
  // popup-disabled fallback (this module never runs when the section is off).
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // keep open-in-new-tab etc.
    const cta = e.target && e.target.closest && e.target.closest('[data-evol-alerts]');
    const ctaSource = cta && cta.getAttribute('data-evol-source');
    if (!cta || opened) return; // mid-close fade: fall through to /pages/notify rather than swallow the click
    const embedEl = root.querySelector('.evol-reel__embed');
    if (embedEl && !embedEl.querySelector('input')) return; // embed not hydrated (slow network / blocker) — use the page fallback
    e.preventDefault();
    if (reopenBtn) reopenBtn.hidden = true;
    openAlerts(ctaSource);
  });

  // Embed mode: the Omnisend multi-step form owns submit/consent/success, so the reel's
  // own finishDone never fires. Bank the claim when the embedded form is submitted so the
  // frequency gating + re-entry widget treat the visitor as claimed. (Verify the exact
  // event name against the live form during preview testing; the matcher is deliberately
  // broad — first email submit already counts as claiming the gift.)
  // After the email step: tell Omnisend the visitor's shop country and where they signed up.
  // The snippet's identify takes email/phone only and the form's tags are fixed per form, so an
  // Omnisend automation ("Popup signup tags") turns this event into tags. origin 'api' is
  // required by the snippet's /track endpoint (400 "origin is required" without it).
  // identifyContact is async and returns nothing, and the snippet silently DROPS a track pushed
  // before it knows the contact, so wait for its omnisendContactID cookie to appear (or switch,
  // on a device already identified as someone else) before pushing.
  const omnisendContactId = function () {
    const m = document.cookie.match(/(?:^|;\s*)omnisendContactID=([^;]+)/);
    return m ? m[1] : '';
  };
  const sendSignupEvent = function () {
    try {
      window.omnisend = window.omnisend || [];
      const emailEl = root.querySelector('.evol-reel__embed input[type="email"]');
      const email = emailEl && emailEl.value ? emailEl.value.trim() : '';
      // Snapshot now: closing the popup resets signupSource.
      const properties = {
        tool: signupSource,
        country: (window.Shopify && window.Shopify.country) || '',
        page: window.location.pathname
      };
      const before = omnisendContactId();
      if (email && typeof window.omnisend.identifyContact === 'function') window.omnisend.identifyContact({ email: email });
      let waited = 0;
      (function send() {
        const id = omnisendContactId();
        if (id && (id !== before || waited >= 3000)) {
          window.omnisend.push(['track', 'popup signup', { origin: 'api', properties: properties }]);
        } else if (waited < 8000) {
          waited += 150;
          setTimeout(send, 150);
        }
      })();
    } catch (e) {}
  };
  const embedHost = root.querySelector('[id^="omnisend-embedded-v2-"]');
  const embedFormId = embedHost ? embedHost.id.replace('omnisend-embedded-v2-', '') : '';
  window.addEventListener('omnisendForms', function (e) {
    try {
      var d = (e && e.detail) || {};
      var t = String(d.eventType || d.type || d.action || (d.action && d.action.type) || '');
      if (t === 'stepSubmit' && d.form && d.form.id === embedFormId) {
        emit(String(d.step) === '1' ? 'evol_reel_email_submit' : 'evol_reel_phone_submit');
        if (String(d.step) === '1') sendSignupEvent();
      }
      if (/subscrib|success|complete|submit/i.test(t)) {
        var st = readState();
        if (!st.claimedAt) { st.claimedAt = Date.now(); writeState(st); }
        if (reopenBtn) reopenBtn.hidden = true;
      }
    } catch (err) {}
  });

  // Frame ↔ embed step sync: the pack image + heading belong to the EMAIL step only.
  // The embed runs email→phone→success inside the reveal step; once it moves past the
  // email field we collapse the frame to full-width (no image) to match the pre-embed
  // phone/done steps. This ONLY toggles a class on the reveal step — it never touches
  // the embedded form, so it can't affect capture.
  let phoneCountrySet = false;
  const setPhoneCountry = function () {
    if (phoneCountrySet) return;
    phoneCountrySet = true;
    const cc = window.Shopify && window.Shopify.country;
    if (!cc || cc === 'GB') return;
    const current = root.querySelector('.evol-reel__embed [class*="-phone-number-prefix-value"]');
    if (current && current.textContent.trim() !== '+44') return; // visitor already chose one
    const opts = root.querySelectorAll('.evol-reel__embed [role="option"]');
    for (let i = 0; i < opts.length; i++) {
      const code = opts[i].querySelector('[class*="-flag-code"] span');
      if (code && code.textContent.trim() === cc) { opts[i].click(); break; }
    }
  };
  (function () {
    const embed = root.querySelector('.evol-reel__embed');
    const revealStep = steps.reveal;
    if (!embed || !revealStep) return;
    let seenEmail = false;
    const sync = function () {
      // Only while the form is on screen: hidden, nothing looks visible, and a mutation then (closing
      // alerts mode removes the button's aria-label) would mark the email step done: reopening showed
      // the email field with the success buttons and no pack or heading (owner, 30 Sep).
      if (root.hidden || revealStep.hidden) return;
      try {
        const em = embed.querySelector('input[type="email"]');
        const emVisible = !!(em && em.offsetParent !== null);
        if (emVisible) seenEmail = true;
        const tel = embed.querySelector('input[type="tel"]');
        const telVisible = !!(tel && tel.offsetParent !== null);
        const advanced = seenEmail && !emVisible;
        revealStep.classList.toggle('is-form-advanced', advanced);   // hide image+heading past email
        revealStep.classList.toggle('is-phone-step', telVisible);    // skip link only here
        if (telVisible) setPhoneCountry();
        revealStep.classList.toggle('is-form-done', advanced && !telVisible); // success/subscribed → continue
        // Country dropdown open? Let the dialog show it (don't clip), then re-clip on close.
        const lb = embed.querySelector('[role="listbox"]');
        const ddOpen = !!(lb && window.getComputedStyle(lb).display !== 'none');
        if (dialog) dialog.classList.toggle('is-dropdown-open', ddOpen);
      } catch (e) {}
    };
    window.addEventListener('omnisendForms', sync);
    try {
      new MutationObserver(sync).observe(embed, { childList: true, subtree: true, attributes: true });
    } catch (e) {}
    formSync = sync;
    sync();
  })();

  // Smooth steps: when the card's height changes (spin -> gift, email -> phone -> success,
  // an error line appearing), animate from the old height instead of snapping. The steps
  // themselves fade in via CSS. Off for reduced motion or where the APIs are missing.
  // Watches the steps wrapper, not the card: its height stays natural while the card's is
  // animated, so a late change (an image landing, Omnisend re-rendering) retargets the running
  // animation from wherever the card is instead of queueing a second jump.
  (function () {
    const panes = dialog && dialog.querySelector('.evol-reel__panes');
    if (!panes || REDUCED || typeof ResizeObserver === 'undefined' || typeof dialog.animate !== 'function') return;
    let lastH = 0;
    let running = null;
    const cardHeight = function (inner) {
      const cs = window.getComputedStyle(dialog);
      const h = inner + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const max = parseFloat(cs.maxHeight);
      return isNaN(max) ? h : Math.min(h, max);
    };
    new ResizeObserver(function () {
      const inner = panes.offsetHeight;
      if (!opened || !inner) { lastH = 0; return; }
      const to = cardHeight(inner);
      if (!lastH || Math.abs(to - lastH) < 2) { lastH = to; return; }
      const from = running ? dialog.getBoundingClientRect().height : lastH;
      if (running) running.cancel();
      lastH = to;
      dialog.classList.add('is-resizing');
      const anim = dialog.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 280, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' });
      running = anim;
      anim.onfinish = anim.oncancel = function () {
        if (running !== anim) return; // a retarget replaced this animation
        running = null;
        dialog.classList.remove('is-resizing');
      };
    }).observe(panes);
  })();

  // Consent first. Shopify's cookie banner covers the popup's button on phones, so the
  // auto-open waits until the visitor has answered it. On wider screens the banner sits
  // below the dialog, so it stops waiting after 20s. Clicks (alerts CTA, re-entry) never wait.
  const bannerUp = function () {
    const b = document.getElementById('shopify-pc__banner');
    return !!(b && b.offsetHeight > 0 && window.getComputedStyle(b).visibility !== 'hidden');
  };
  let consentWait = null;
  const autoOpen = function () {
    if (opened || consentWait) return;
    if (!bannerUp()) { open(); return; }
    teardownTriggers();
    const waitStart = Date.now();
    const narrow = window.matchMedia('(max-width: 749px)').matches;
    consentWait = setInterval(function () {
      const answered = !bannerUp();
      if (!answered && (narrow || Date.now() - waitStart < 20000)) return;
      clearInterval(consentWait);
      setTimeout(open, answered ? 1200 : 0);
    }, 500);
  };

  // ---- Gating + triggers (FORCE bypasses everything) ----
  if (FORCE) {
    openMode = 'force';
    open();
  } else {
    const path = window.location.pathname || '';
    const excluded = CFG.autoOpen === false || (CFG.excludePaths || []).some(function (p) { return path.indexOf(p) !== -1; });
    const st = readState();
    const claimed = st.claimedAt && NOW - st.claimedAt < (CFG.frequencyDaysClaimed || 90) * DAY;
    const seen = st.seenAt && NOW - st.seenAt < (CFG.frequencyDays || 7) * DAY;
    if (claimed || excluded) {
      // no popup, no widget
    } else if (seen) {
      showReopen(); // returning dismisser — offer re-entry instead of auto-opening
    } else {
      setTimeout(warmReel, 2500);
      if (CFG.delaySeconds) delayTimer = setTimeout(autoOpen, CFG.delaySeconds * 1000);
      if (CFG.scrollPercent) {
        scrollHandler = function () {
          const h = document.documentElement;
          const max = (h.scrollHeight - h.clientHeight) || 1;
          if (((h.scrollTop || document.body.scrollTop) / max) * 100 >= CFG.scrollPercent) autoOpen();
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
      }
      if (CFG.exitIntent && window.matchMedia('(min-width: 750px)').matches) {
        exitHandler = function (e) { if (!e.relatedTarget && e.clientY <= 0) autoOpen(); };
        document.addEventListener('mouseout', exitHandler);
      }
    }
  }
})();

/* =========================================================
   PDP: stock-badge qty sync + mobile sticky add-to-cart bar
   (Replaces the inline <script> that previously lived in
   templates/product.json — dead variant:change / [data-product]
   paths pruned: the store is single-variant, the Liquid-rendered
   badge is already correct server-side. The only live behaviour
   was hiding the quantity selector when out of stock.)
   ========================================================= */
(function () {
  var badge = document.getElementById('ev-stock-badge');
  if (badge) {
    var qtyEl = document.querySelector('.product-form__quantity')
      || document.querySelector('.quantity')
      || document.querySelector('quantity-input');
    if (qtyEl && badge.classList.contains('out-stock')) qtyEl.classList.add('hidden');
  }

  /* Sticky add-to-cart bar (mobile only). Buy buttons sit on screen 2 of a
     ~9-screen page — returning readers (chase cards, reviews) get a one-tap
     CTA instead of a 5-screen scroll. Shown only after the buy zone scrolls
     out of view; never built for sold-out products. The bar's button clicks
     the REAL form submit, so cart drawer + gift reconciler behave normally. */
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  var realSubmit = document.querySelector('.product-form__submit');
  var infoZone = document.querySelector('.product__info-wrapper');
  if (!realSubmit || realSubmit.disabled || !infoZone) return;
  // Pre-orders (7 Oct 2026): Essential Pre-Order shrinks the theme's submit to nothing and adds its own visible
  // "Preorder" submit to the same form, so the bar watches the whole form and uses whichever submit is visible.
  var buyForm = realSubmit.closest('form') || realSubmit;
  var visibleSubmit = function () {
    var all = document.querySelectorAll('.product-form__submit');
    for (var i = 0; i < all.length; i++) if (all[i].getBoundingClientRect().height > 0) return all[i]; // the app shrinks the theme's button by transform
    return all[0] || null;
  };

  var titleEl = document.querySelector('.product__title h1, h1.product__title');
  var priceEl = document.querySelector('.product__info-wrapper .price__regular .price-item--regular, .product__info-wrapper .price-item--regular');
  var bar = document.createElement('div');
  bar.className = 'evol-sticky-atc';
  bar.hidden = true;
  bar.innerHTML =
    '<div class="evol-sticky-atc__meta">' +
      '<span class="evol-sticky-atc__title"></span>' +
      '<span class="evol-sticky-atc__price"></span>' +
    '</div>' +
    '<button type="button" class="evol-sticky-atc__btn">Add to cart</button>';
  bar.querySelector('.evol-sticky-atc__title').textContent = titleEl ? titleEl.textContent.trim() : '';
  bar.querySelector('.evol-sticky-atc__price').textContent = priceEl ? priceEl.textContent.trim() : '';
  document.body.appendChild(bar);

  bar.querySelector('.evol-sticky-atc__btn').addEventListener('click', function () {
    var btn = visibleSubmit();
    if (btn && !btn.disabled) btn.click();
  });

  var show = function (visible) {
    if (bar.hidden === !visible) return;
    if (visible) {
      var label = visibleSubmit();
      bar.querySelector('.evol-sticky-atc__btn').textContent = (label && label.innerText.trim()) || 'Add to cart';
    }
    bar.hidden = !visible;
    document.body.classList.toggle('evol-sticky-atc-open', visible);
  };
  try {
    new IntersectionObserver(function (entries) {
      // Use the LATEST notification (entries are chronological). Show only once the
      // buy zone has scrolled ABOVE the viewport (not before the user reaches it);
      // hide again if the submit becomes disabled.
      var entry = entries[entries.length - 1];
      var btn = visibleSubmit();
      var soldOut = !btn || btn.disabled;
      show(!entry.isIntersecting && entry.boundingClientRect.top < 0 && !soldOut);
    }, { rootMargin: '0px 0px -40px 0px' }).observe(buyForm);
  } catch (e) { /* no observer support — no bar */ }
})();

/* =============================================
   Supplier partners page — form modal
   Opens on [data-evol-supplier-open], closes on overlay / X / Escape.
   Re-opens automatically after Shopify contact POST re-render when the
   success block (or a validation error) is present in the markup.
   ============================================= */
(function () {
  var modal = document.getElementById('EvolSupplierModal');
  if (!modal) return;
  // Direct child of body so the page-blur selector can exclude it (reel pattern)
  document.body.appendChild(modal);

  function open() {
    modal.hidden = false;
    document.body.classList.add('evol-supplier-modal-open');
    var first = modal.querySelector('input:not([type="hidden"]), textarea');
    if (first) setTimeout(function () { first.focus(); }, 80);
  }
  function close() {
    modal.hidden = true;
    document.body.classList.remove('evol-supplier-modal-open');
  }

  document.querySelectorAll('[data-evol-supplier-open]').forEach(function (btn) {
    btn.addEventListener('click', open);
  });
  modal.querySelectorAll('[data-evol-supplier-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });
  if (modal.querySelector('[data-evol-supplier-success]') || modal.querySelector('.evol-supplier-modal__error')) {
    open();
  }
})();

/* =============================================
   Supplier partners — sync step-dot glow to the rail pulse.
   The sweep travels the track (top -120px -> 100%) over 6.5s; each dot's
   glow delay is computed from its measured centre on the rail so the dot
   lights exactly as the pulse centre passes, at any breakpoint.
   ============================================= */
(function () {
  var stepper = document.querySelector('.evol-supplier__stepper');
  if (!stepper) return;
  var PERIOD = 6.5, PULSE = 120, LEAD = 0.26; /* glow peaks 4% into its 6.5s cycle */

  function sync() {
    var track = stepper.querySelector('.evol-supplier__steps-track');
    if (!track) return;
    var tr = track.getBoundingClientRect();
    if (!tr.height) return;
    stepper.querySelectorAll('.evol-supplier__step-dot').forEach(function (dot) {
      var d = dot.getBoundingClientRect();
      var y = d.top + d.height / 2 - tr.top;
      var t = PERIOD * (y + PULSE / 2 + 5) / (tr.height + PULSE) - LEAD;
      while (t < 0) t += PERIOD;
      dot.style.setProperty('--evol-sup-glow-delay', t.toFixed(2) + 's');
    });
  }

  sync();
  window.addEventListener('load', sync);
  var raf;
  window.addEventListener('resize', function () {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(sync);
  });
})();

/* =============================================
   Supplier partners — phone dial-code selector + per-country validation.
   The dial select doubles as the business-country field (synced to the
   hidden contact[Country]); non-empty numbers must match the country's
   national-number length range; submits in international format.
   ============================================= */
(function () {
  var group = document.querySelector('[data-evol-phone]');
  if (!group) return;
  var form = group.closest('form');
  var dial = group.querySelector('[data-evol-phone-dial]');
  var input = group.querySelector('[data-evol-phone-input]');
  var country = form.querySelector('[data-evol-phone-country]');
  var error = form.querySelector('[data-evol-phone-error]');

  function opt() { return dial.options[dial.selectedIndex]; }
  function digits() {
    var d = input.value.replace(/[^0-9]/g, '');
    return d.replace(/^0+/, '');
  }
  function valid() {
    if (!input.value.trim()) return true;
    var d = digits();
    var min = parseInt(opt().getAttribute('data-min'), 10);
    var max = parseInt(opt().getAttribute('data-max'), 10);
    if (isNaN(min) || isNaN(max)) return false; // number entered before a country is chosen
    return d.length >= min && d.length <= max;
  }
  function paint(ok) {
    group.classList.toggle('is-invalid', !ok);
    if (error) error.hidden = ok;
  }
  function syncCountry() {
    if (country) country.value = opt().value;
  }

  // Size the dial box to its current value so the chevron sits right after the code.
  var measure = document.createElement('span');
  measure.className = 'evol-supplier-modal__dial-measure';
  document.body.appendChild(measure);
  function fitDial() {
    var cs = getComputedStyle(dial);
    measure.style.font = cs.font;
    measure.style.fontFamily = cs.fontFamily;
    measure.style.fontSize = cs.fontSize;
    measure.style.fontWeight = cs.fontWeight;
    measure.style.letterSpacing = cs.letterSpacing;
    measure.textContent = opt().textContent.trim();
    dial.style.width = Math.ceil(measure.offsetWidth + 40) + 'px';
  }

  dial.addEventListener('change', function () {
    syncCountry();
    fitDial();
    paint(valid());
  });
  input.addEventListener('input', function () {
    input.value = input.value.replace(/[^0-9+()\-\s]/g, '');
    if (group.classList.contains('is-invalid')) paint(valid());
  });
  input.addEventListener('blur', function () { paint(valid()); });

  form.addEventListener('submit', function (e) {
    syncCountry();
    if (!valid()) {
      e.preventDefault();
      paint(false);
      input.focus();
      return;
    }
    var code = opt().getAttribute('data-dial');
    if (input.value.trim() && code) {
      input.value = '+' + code + ' ' + digits();
    }
  });
  syncCountry();
  fitDial();
  window.addEventListener('load', fitDial);
})();

/* ==========================================================================
   Evol: Verified Reviews — two-row passive marquee
   --------------------------------------------------------------------------
   Reads the inert Judge.me review list rendered by section-evol-reviews,
   rebuilds each review as our own card, deals them alternately into two tracks,
   then duplicates each track so the CSS animation can loop seamlessly.

   The scrolling itself is pure CSS. This only extracts data and clones nodes.
   Dates are formatted en-GB here, so the app's mm/dd/yyyy setting is bypassed.
   ========================================================================== */
(function () {
  var root = document.querySelector('[data-evol-reviews]');
  if (!root) return;

  var source = root.querySelector('.evol-reviews__source');
  var trackA = root.querySelector('[data-track="a"]');
  var trackB = root.querySelector('[data-track="b"]');
  if (!source || !trackA || !trackB) return;

  var minScore = parseInt(root.getAttribute('data-min-score'), 10) || 4;
  var speed = parseInt(root.getAttribute('data-speed'), 10) || 70;
  var showImages = root.getAttribute('data-show-images') === 'true';
  // Thin one-liners ("Spot on", "Awesome") leave dead space in a fixed-height
  // row and add no persuasion. Filtering by body length evens the cards out.
  var minLen = parseInt(root.getAttribute('data-min-length'), 10) || 40;
  // Judge.me only flags data-verified-buyer when it can auto-match the reviewer
  // to an order. Reviews that skipped the confirmation email land unflagged even
  // though the buyer is real. The owner has confirmed every review here against
  // order records, so the badge is shown on all of them.
  var verifyAll = root.getAttribute('data-verify-all') === 'true';

  var text = function (el, sel) {
    var n = el.querySelector(sel);
    return n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
  };

  var reviews = [];
  Array.prototype.forEach.call(source.querySelectorAll('.jdgm-rev'), function (rev) {
    var ratingEl = rev.querySelector('.jdgm-rev__rating');
    var score = ratingEl ? parseInt(ratingEl.getAttribute('data-score'), 10) : 0;
    var body = text(rev, '.jdgm-rev__body');
    if (!score || score < minScore || body.length < minLen) return;

    var stamp = rev.querySelector('.jdgm-rev__timestamp');
    var iso = stamp ? stamp.getAttribute('data-content') : '';
    var date = '';
    if (iso) {
      var d = new Date(iso.replace(' UTC', 'Z').replace(' ', 'T'));
      if (!isNaN(d)) {
        date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      }
    }

    // Photos ride along only on 5-star reviews that also carry written text.
    // A quality proxy rather than a hand-picked allowlist, so new photo reviews
    // qualify automatically and weak snapshots stay out.
    var pic = '';
    if (showImages && score === 5 && body.length > 24) {
      var img = rev.querySelector('.jdgm-rev__pic img, .jdgm-rev__pics img, [data-src]');
      if (img) pic = img.getAttribute('data-src') || img.getAttribute('src') || '';
      if (pic) pic = pic.replace(/&amp;/g, '&').replace(/width=\d+/, 'width=320');
    }

    reviews.push({
      score: score,
      body: body,
      author: text(rev, '.jdgm-rev__author') || text(rev, '.jdgm-rev__buyer-name') || 'Verified buyer',
      product: text(rev, '.jdgm-rev__prod-link'),
      date: date,
      pic: pic,
      verified: verifyAll || rev.getAttribute('data-verified-buyer') === 'true'
    });
  });

  // One card per NAMED reviewer: repeat buyers leave several reviews (Sílvio x3
  // as of Aug 2026) and the same name recurring reads as duplication. The list
  // arrives newest-first, so first-seen = their most recent review. "Anonymous"
  // is exempt — those are DIFFERENT customers sharing Judge.me's fallback label,
  // and collapsing them would throw away real distinct reviews. Identical bodies
  // are dropped regardless of author as insurance against true data dupes.
  var seenAuthors = {};
  var seenBodies = {};
  reviews = reviews.filter(function (r) {
    var a = r.author.replace(/\s+/g, ' ').trim().toLowerCase();
    var b = r.body.toLowerCase();
    if (seenBodies[b]) return false;
    seenBodies[b] = true;
    if (a && a !== 'anonymous' && a !== 'verified buyer') {
      if (seenAuthors[a]) return false;
      seenAuthors[a] = true;
    }
    return true;
  });

  if (reviews.length < 4) {
    root.setAttribute('hidden', '');
    return;
  }

  var buildCard = function (r) {
    var card = document.createElement('article');
    card.className = 'evol-rev-card' + (r.pic ? ' evol-rev-card--has-pic' : '');

    if (r.pic) {
      var figure = document.createElement('div');
      figure.className = 'evol-rev-card__pic';
      var im = document.createElement('img');
      im.src = r.pic;
      im.loading = 'lazy';
      im.decoding = 'async';
      im.alt = '';
      figure.appendChild(im);
      card.appendChild(figure);
    }

    var main = document.createElement('div');
    main.className = 'evol-rev-card__main';

    var stars = document.createElement('div');
    stars.className = 'evol-rev-card__stars';
    stars.setAttribute('aria-label', r.score + ' out of 5 stars');
    for (var i = 0; i < 5; i++) {
      var s = document.createElement('span');
      s.className = 'evol-rev-card__star' + (i < r.score ? '' : ' is-empty');
      s.textContent = '★';
      stars.appendChild(s);
    }
    main.appendChild(stars);

    var body = document.createElement('p');
    body.className = 'evol-rev-card__body';
    body.textContent = r.body;
    main.appendChild(body);

    var foot = document.createElement('div');
    foot.className = 'evol-rev-card__foot';
    var who = document.createElement('span');
    who.className = 'evol-rev-card__author';
    who.textContent = r.author;
    foot.appendChild(who);
    if (r.verified) {
      var v = document.createElement('span');
      v.className = 'evol-rev-card__verified';
      v.textContent = 'Verified';
      foot.appendChild(v);
    }
    if (r.date) {
      var dt = document.createElement('span');
      dt.className = 'evol-rev-card__date';
      dt.textContent = r.date;
      foot.appendChild(dt);
    }
    main.appendChild(foot);

    card.appendChild(main);
    return card;
  };

  // Deal alternately so photo cards and long/short bodies spread across both rows
  // rather than clumping into one.
  var rowA = [];
  var rowB = [];
  reviews.forEach(function (r, i) {
    (i % 2 === 0 ? rowA : rowB).push(r);
  });

  var fill = function (track, list) {
    // Twice through: the keyframes translate by -50%, so the second copy is
    // exactly where the first started and the loop has no visible seam.
    for (var pass = 0; pass < 2; pass++) {
      list.forEach(function (r) {
        var card = buildCard(r);
        if (pass === 1) card.setAttribute('aria-hidden', 'true');
        track.appendChild(card);
      });
    }
    track.style.animationDuration = speed + 's';
  };

  fill(trackA, rowA);
  fill(trackB, rowB);

  source.parentNode.removeChild(source);
  root.classList.add('is-ready');
})();

/* Header stack height -> CSS var (sticky offsets for anything pinned under the header). */
(function () {
  var stack = document.querySelector('.evol-header-stack');
  if (!stack) return;
  var set = function () { document.documentElement.style.setProperty('--evol-stack-h', stack.offsetHeight + 'px'); };
  set();
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(stack);
  else window.addEventListener('resize', set);
})();
